/* ExpandableGridRowRenderer.js */

"use strict";

const _ = require('lodash');
// const GridCell = require('../cell/GridCellComponent');
// const GridRow = require('./ExpandableGridRowComponent');

class GridRowRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("expandable-grid-row");
        this._rootElem.component = this._model;
        this._rootElem.tabIndex = -1;

        this._expandCollapseIcon = document.createElement('toggle-icon-gl');
        this._expandCollapseIcon.dataset.icon = 'toggle.open';
        this._expandCollapseIcon.classList.add('center-vertically');

        this._innerCell = document.createElement('grid-cell');
        this._innerCell.classList.add('expandable-cell');

        this._innerCellIcon = document.createElement('base-icon-gl');
        this._innerCellIcon.dataset.icon = 'common.folder';
        this._innerCellIcon.classList.add('center-vertically');

        this._innerCellText = document.createElement('span');
        this._innerCellText.classList.add('inner-cell-text');

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown(){
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._textField = undefined;
            this._rootElem = undefined;

            this._expandCollapseIcon = undefined;
            this._innerCell = undefined;
            this._innerCellIcon = undefined;
            this._innerCellText = undefined;

            resolve();
        });
        return prom;
    }
    _registerListeners(){
        this._model.addListener("ID",this._onId = this._onId.bind(this));
        this._model.addListener("Name",this._onName = this._onName.bind(this));
        this._model.addListener("ColumnOrder",this._onColumnOrder = this._onColumnOrder.bind(this));
        this._model.addListener("ColumnComponents",this._onColumnComponents = this._onColumnComponents.bind(this));
        this._model.addListener("Children",this._onChildren = this._onChildren.bind(this));
        this._model.addListener("NestedLevel",this._onNestedLevel = this._onNestedLevel.bind(this));
        this._model.addListener("Expanded",this._onExpanded = this._onExpanded.bind(this));
        this._rootElem.addEventListener('click', this._onRowClick = this._onRowClick.bind(this));
        this._rootElem.addEventListener('dblclick', this._onRowDoubleClick = this._onRowDoubleClick.bind(this));
        this._rootElem.addEventListener('mouseenter', this._onMouseEnter = this._onMouseEnter.bind(this));
        this._rootElem.addEventListener('mouseleave', this._onMouseLeave = this._onMouseLeave.bind(this));
        this._expandCollapseIcon.addEventListener('click', this._onIconClick = this._onIconClick.bind(this));
    }
    _unregisterListeners(){
        this._model.removeListener("ID",this._onId);
        this._model.removeListener("Name",this._onName);
        this._model.removeListener("ColumnOrder",this._onColumnOrder);
        this._model.removeListener("ColumnComponents",this._onColumnComponents);
        this._model.removeListener("Children",this._onChildren);
        this._model.removeListener("NestedLevel",this._onNestedLevel);
        this._model.removeListener("Expanded",this._onExpanded);
        this._rootElem.removeEventListener('click', this._onRowClick);
        this._rootElem.removeEventListener('dblclick', this._onRowDoubleClick);
        this._rootElem.removeEventListener('mouseenter', this._onMouseEnter);
        this._rootElem.removeEventListener('mouseleave', this._onMouseLeave);
        this._expandCollapseIcon.removeEventListener('click', this._onIconClick);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(){
        let prom = new Promise((resolve/*,reject*/) => {
            this._innerCell.appendChild(this._expandCollapseIcon);
            this._innerCell.appendChild(this._innerCellIcon);
            this._innerCell.appendChild(this._innerCellText);
            this._rootElem.appendChild(this._innerCell);
            this._setId(this._model.getId());
            this._setName(this._model.getName());
            this._setNestedLevel(this._model.getNestedLevel());
            this._setExpanded(this._model.getExpanded());
            resolve();
        });
        return prom;
    }
    get rootElement(){
        return this.getRootElement();
    }
    getRootElement(){
        return this._rootElem;
    }
    get model(){
        return this.getModel();
    }
    getModel(){
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the unique key for the row has changed
     * @param newVal {string}
     * @private
     */
    _onId(newVal){
        this._setId(newVal);
    }

    /**
     * Fired when the name has changed
     * @param newVal {object}
     * @private
     */
    _onName(newVal){
        this._setName(newVal);
    }

    /**
     * Fired when the grid column order has changed
     * @param newVal {array}
     * @private
     */
    _onColumnOrder(newVal){
        this._setColumnOrder(newVal);
    }

    /**
     * Fired when the grid columns have changed
     * @param newVal {object}
     * @private
     */
    _onColumnComponents(newVal){
        this._setColumns(newVal);
    }

    /**
     * Fired when the row's child elements have been updated in the model
     * @param newVal {array}
     * @private
     */
    _onChildren(newVal){
        this._setChildren(newVal);
    }

    /**
     * Fired when the nested level has been updated in the model
     * @param newVal {array}
     * @private
     */
    _onNestedLevel(newVal){
        this._setNestedLevel(newVal);
    }

    /**
     * Fired when the expanded state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onExpanded(newVal){
        this._setExpanded(newVal);
    }

    /**
     * Fired when a row has been selected by the user
     * @param event {event}
     * @private
     */
    _onRowClick(event){
        let expanded = this._model.getExpanded();
        this._model._triggerRowUserSelected(event);
        this._model.setExpanded(!expanded);
        this._model._triggerRowUserExpanded();
    }

    /**
     * Fired when a row has been selected by the user
     * @param event {event}
     * @private
     */
    _onRowDoubleClick(event){
        this._model._triggerRowUserDoubleClicked(event);
    }

    /**
     * Fired when the user has entered the row with the mouse
     * @param event {event}
     * @private
     */
    _onMouseEnter(/*event*/){
        this._rootElem.classList.add('active');
    }

    /**
     * Fired when the user has left the row with the mouse
     * @param event {event}
     * @private
     */
    _onMouseLeave(/*event*/){
        this._rootElem.classList.remove('active');
    }

    /**
     * Fired when the expand/collapse icon is clicked
     * @param event {event}
     * @private
     */
    _onIconClick(/*event*/){

    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set the row id in the DOM somewhere?
     * @param newVal {string}
     * @private
     */
    _setId(newVal){
        this._rootElem.dataset.id = newVal;
    }

    /**
     * Set the row id in the DOM somewhere?
     * @param newVal {string}
     * @private
     */
    _setName(newVal){
        if (!_.isUndefined(newVal)) {
            this._innerCellText.innerHTML = newVal;
        } else {
            this._innerCellText.innerHTML = '';
        }
    }



    /**
     * Build the row display
     * @param newVal {object}
     * @private
     */
    // _setRowData(newVal){
    //     this._cells.forEach((cell) => {
    //         cell.removeAllListeners('CellAction');
    //         cell.shutdown();
    //     });
    //     this._cells = [];
    //     let columnOrder = this._model.getColumnOrder(),
    //         columnComponents = this._model.getColumnComponents();
    //     if (columnOrder.length > 0) {
    //         columnOrder.forEach((columnKey) => {
    //             let cell = new GridCell({
    //                 id: columnKey,
    //                 columnRendererComponent: columnComponents[columnKey].getColumnRendererComponent(),
    //                 value: newVal[columnKey],
    //                 width: columnComponents[columnKey].getWidth()
    //             });
    //             this._cells.push(cell);
    //             cell.addListener('CellAction', this._onCellAction = this._onCellAction.bind(this));
    //             this._rootElem.appendChild(cell.renderer.rootElement);
    //         });
    //     } else {
    //         for (let columnKey in newVal) {
    //             let cell = new GridCell({
    //                 id: columnKey,
    //                 columnRendererComponent: columnComponents[columnKey].getColumnRendererComponent(),
    //                 value: newVal[columnKey],
    //                 width: columnComponents[columnKey].getWidth()
    //             });
    //             this._cells.push(cell);
    //             cell.addListener('CellAction', this._onCellAction = this._onCellAction.bind(this));
    //             this._rootElem.appendChild(cell.renderer.rootElement);
    //         }
    //     }
    // }

    /**
     * Rebuild the row with the new column order
     * @param newVal {array}
     * @private
     */
    _setColumnOrder(/*newVal*/){
        console.info('COLUMN ORDER');
        //this._setRowData(this._model.getRowData());
    }

    /**
     * Rebuild the row with the new columns
     * @param newVal {array}
     * @private
     */
    _setColumns(/*newVal*/){
        console.info('COLUMNS');
        //this._setRowData(this._model.getRowData());
    }

    /**
     * Set the nested level
     * @param newVal {array}
     * @private
     */
    _setNestedLevel(newVal){
        this._rootElem.classList.add('nested_' + newVal);
    }

    /**
     * Build/Rebuild child row elements
     * @param newVal {array}
     * @private
     */
    _setChildren(/*newVal*/){
        // Here if needed
    }

    /**
     * Toggle the expanded state
     * @param newVal {boolean}
     * @private
     */
    _setExpanded(newVal){
        if (newVal === true) {
            this._expandCollapseIcon.setToggled(true);
        } else {
            this._expandCollapseIcon.setToggled(false);
        }
    }


    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = GridRowRenderer;